import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { GraduationCap, BarChart3, Shield, Clock } from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-blue-50">
      {/* Header */}
      <header className="border-b border-sky-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-8 w-8 text-sky-600" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-sky-600 to-blue-600 bg-clip-text text-transparent" style={{fontFamily: 'Space Grotesk, sans-serif'}}>ESAP</h1>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => navigate('/login')}
              data-testid="header-login-btn"
              className="border-sky-200 hover:bg-sky-50"
            >
              Login
            </Button>
            <Button 
              onClick={() => navigate('/register')}
              data-testid="header-register-btn"
              className="bg-sky-600 hover:bg-sky-700"
            >
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        <div className="text-center space-y-6 animate-fade-in">
          <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Online Examination
            <span className="block bg-gradient-to-r from-sky-600 to-blue-600 bg-clip-text text-transparent mt-2">
              Made Simple
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
            A modern, secure platform for conducting online exams with real-time monitoring,
            auto-save functionality, and comprehensive analytics.
          </p>
          <div className="flex gap-4 justify-center mt-8">
            <Button 
              size="lg" 
              onClick={() => navigate('/register')}
              data-testid="hero-get-started-btn"
              className="bg-sky-600 hover:bg-sky-700 text-lg px-8 py-6 rounded-full"
            >
              Get Started Free
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => navigate('/login')}
              data-testid="hero-login-btn"
              className="border-sky-200 hover:bg-sky-50 text-lg px-8 py-6 rounded-full"
            >
              Login
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h3 className="text-3xl sm:text-4xl font-bold text-center mb-16 text-gray-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Why Choose ESAP?</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          <FeatureCard
            icon={<Shield className="h-8 w-8 text-sky-600" />}
            title="Secure & Reliable"
            description="JWT-based authentication with auto-submit and session management"
          />
          <FeatureCard
            icon={<Clock className="h-8 w-8 text-sky-600" />}
            title="Real-time Timer"
            description="Automatic submission when time expires with resume capability"
          />
          <FeatureCard
            icon={<BarChart3 className="h-8 w-8 text-sky-600" />}
            title="Analytics Dashboard"
            description="Comprehensive insights on performance and question difficulty"
          />
          <FeatureCard
            icon={<GraduationCap className="h-8 w-8 text-sky-600" />}
            title="Student Friendly"
            description="Intuitive interface with question palette and mark for review"
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-sky-600 to-blue-600 py-20">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h3 className="text-4xl font-bold text-white mb-6" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Ready to Transform Your Exams?
          </h3>
          <p className="text-sky-100 text-lg mb-8">
            Join thousands of educators and students using ESAP for seamless online examinations.
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate('/register')}
            data-testid="cta-register-btn"
            className="bg-white text-sky-600 hover:bg-gray-50 text-lg px-8 py-6 rounded-full"
          >
            Start Your Free Trial
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-gray-600">
          <p>&copy; 2025 ESAP. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-sky-100 hover:border-sky-200 hover:shadow-lg transition-all">
      <div className="mb-4">{icon}</div>
      <h4 className="text-xl font-semibold mb-2 text-gray-900">{title}</h4>
      <p className="text-gray-600">{description}</p>
    </div>
  );
}